import { useMachine } from '@xstate/react';
import type { Side } from '@brig/rules-engine';
import { useEffect, useState } from 'react';
import type { History } from './history.ts';
import { turnMachine, type localCommit, type View } from './turnMachine.ts';

/** Whoever keeps the game: the server (game/server.ts), or the browser's own engine. */
export type CommitLogic = typeof localCommit;

const SETTINGS_KEY = 'brig.settings.v1';

/** Per-device settings (docs/ux.md §14). */
interface Settings {
  readonly confirmCast: boolean;
  readonly view: View;
}

function loadSettings(): Settings {
  try {
    const saved = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}') as Partial<Settings>;
    return { confirmCast: saved.confirmCast ?? true, view: saved.view === 'chart' ? 'chart' : 'perspective' };
  } catch {
    return { confirmCast: true, view: 'perspective' };
  }
}

function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Storage may be unavailable (private mode); settings then last for the session.
  }
}

function prefersReducedMotion(): boolean {
  // jsdom has no matchMedia.
  return (
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** The turn machine for one game screen, with its settings kept on the device. */
export function useTurn(initial: () => History, commit?: CommitLogic, remote: Side | null = null) {
  // The machine reads its input once; don't rebuild the history on every render.
  const [input] = useState(() => {
    const settings = loadSettings();
    return { history: initial(), ...settings, animate: !prefersReducedMotion(), remote };
  });
  const [machine] = useState(() => (commit ? turnMachine.provide({ actors: { commit } }) : turnMachine));
  const [snapshot, send, actor] = useMachine(machine, { input });
  const { confirmCast, view } = snapshot.context;
  useEffect(() => {
    saveSettings({ confirmCast, view });
  }, [confirmCast, view]);
  return { snapshot, send, actor };
}

export type TurnSnapshot = ReturnType<typeof useTurn>['snapshot'];
export type TurnSend = ReturnType<typeof useTurn>['send'];
